<template>
    <GenericModal
        :modelValue="showModal"
        @update:modelValue="handleModalClose"
        :closable="true"
        :fullscreenMobile="true"
        :darkMode="true"
        contentPadding="0"
    >
        <template #header>
            <LanguageSelector :fullDetail="true" />
        </template>
        <div class="how-it-works-modal">
            <div class="modal-body">
                <h1 class="modal-title">
                    {{ $t('how_it_works.title') }}
                </h1>

                <div class="features-container">
                    <div class="feature-item">
                        <div class="feature-icon">
                            <i class="pi pi-bolt"></i>
                        </div>
                        <div class="feature-content">
                            <h3 class="feature-title">
                                {{ $t('how_it_works.features.try_free.title') }}
                            </h3>
                            <p class="feature-subtitle">
                                {{
                                    $t(
                                        'how_it_works.features.try_free.subtitle'
                                    )
                                }}
                            </p>
                        </div>
                    </div>

                    <div class="feature-item">
                        <div class="feature-icon">
                            <i class="pi pi-star"></i>
                        </div>
                        <div class="feature-content">
                            <h3 class="feature-title">
                                {{
                                    $t(
                                        'how_it_works.features.paid_services.title'
                                    )
                                }}
                            </h3>
                            <p class="feature-subtitle">
                                {{
                                    $t(
                                        'how_it_works.features.paid_services.subtitle'
                                    )
                                }}
                            </p>
                        </div>
                    </div>

                    <div class="feature-item">
                        <div class="feature-icon">
                            <i class="pi pi-lock"></i>
                        </div>
                        <div class="feature-content">
                            <h3 class="feature-title">
                                {{ $t('how_it_works.features.privacy.title') }}
                            </h3>
                            <p class="feature-subtitle">
                                {{
                                    $t('how_it_works.features.privacy.subtitle')
                                }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="checkbox-container">
                    <label class="checkbox-label">
                        <input
                            type="checkbox"
                            v-model="agreedToTerms"
                            class="checkbox-input"
                        />
                        <span
                            class="checkbox-text"
                            v-html="$t('how_it_works.age_confirmation')"
                        ></span>
                    </label>
                </div>

                <div class="modal-actions">
                    <button
                        class="btn-primary"
                        :disabled="!agreedToTerms"
                        @click="handleContinue"
                    >
                        {{ $t('how_it_works.continue') }}
                    </button>
                </div>
            </div>
        </div>
    </GenericModal>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import GenericModal from '@/components/shared/core/GenericModal.vue'
import LanguageSelector from '@/components/shared/LanguageSelector.vue'

// Router
const router = useRouter()

// State
const showModal = ref(false)
const agreedToTerms = ref(false)

// Methods
const handleContinue = () => {
    if (agreedToTerms.value) {
        showModal.value = false
        router.push('/client/profiles')
    }
}

const handleModalClose = (value) => {
    showModal.value = value
    // Reset checkbox when modal is closed
    if (!value) {
        agreedToTerms.value = false
    }
}

// Expose methods for external control
defineExpose({
    show: () => {
        showModal.value = true
        agreedToTerms.value = false
    },
    hide: () => {
        showModal.value = false
        agreedToTerms.value = false
    },
})
</script>

<style scoped>
.how-it-works-modal {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    color: white;
    position: relative;
}

.modal-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 80px 30px 40px;
    gap: 32px;
    max-width: 400px;
    margin: 0 auto;
    flex: 1;
}

.modal-title {
    font-family: 'Basier Circle', sans-serif;
    font-weight: 400;
    font-size: 30px;
    line-height: 38px;
    color: white;
    text-wrap: balance;
    margin: 0;
    text-align: center;
}

.features-container {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    border: 1px solid #707070;
    border-radius: 20px;
    padding: 24px;
}

.feature-item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    text-align: left;
}

.feature-icon {
    flex-shrink: 0;
}

.feature-icon i {
    font-size: 20px;
}

.feature-content {
    flex: 1;
}

.feature-title {
    font-family: 'Nunito', sans-serif;
    font-weight: 600;
    font-size: 18px;
    line-height: 20px;
    color: white;
    margin: 0 0 4px 0;
}

.feature-subtitle {
    font-family: 'Nunito', sans-serif;
    font-weight: 400;
    font-size: 13px;
    line-height: 20px;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
}

.checkbox-container {
    width: 100%;
}

.checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    text-align: left;
}

.checkbox-input {
    margin-top: 2px;
    width: 18px;
    height: 18px;
    cursor: pointer;
    flex-shrink: 0;
}

.checkbox-text {
    font-family: 'Nunito', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: rgba(255, 255, 255, 0.8);
}

.checkbox-text * {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: underline;
}

.modal-actions {
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 100%;
    max-width: 274px;
}

.btn-primary {
    padding: 15px 20px;
    background: linear-gradient(143deg, #6e3dea 0%, #9a55f2 100%);
    border-radius: 10px;
    font-weight: 600;
    border: none;
    transition: all 0.3s;
    text-align: center;
    font-size: 15px;
    font-family: 'Basier Circle', sans-serif;
    color: white;
    cursor: pointer;
}

.btn-primary:hover:not(:disabled) {
    opacity: 0.9;
}

.btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Desktop styles */
@media (min-width: 768px) {
    .modal-body {
        max-width: 420px;
        gap: 40px;
        padding: 100px 40px 40px;
    }

    .modal-title {
        font-size: 32px;
        line-height: 38px;
    }

    .features-container {
        gap: 24px;
        padding: 24px;
    }

    .feature-title {
        font-size: 18px;
        line-height: 22px;
    }

    .feature-subtitle {
        font-size: 16px;
        line-height: 22px;
    }

    .checkbox-text {
        font-size: 15px;
        line-height: 22px;
    }
}
</style>
